/**
 * A commercial building is not somebody's home, and the document should not
 * say it is.
 *
 * Jonathon, 2026-08-27: "commercial presentations shouldn't mention things
 * like homeowners, your home, etc."
 *
 * The copy this app hands a customer was written for a re-roof sold across a
 * kitchen table. "Best for: Homeowners who never want to think about their
 * roof again" reads as warmth to the person it was written for and as a
 * mail-merge accident to a property manager buying four buildings — the same
 * mistake as [ADR 0065]'s "…and Your Home" heading, which was fixed one
 * heading at a time. This is the general form of that fix.
 *
 * ## Why a substitution rather than a second set of words
 *
 * Every one of these strings is editable. The package copy lives in
 * `package_copy` and the Tools screen rewrites it; the wording blocks live on
 * `estimate_items.note` and the Present screen rewrites those. A second
 * commercial column beside each of them would be a second thing to keep in
 * step, and it would go stale the first time somebody edited only one — which
 * is the failure the reference-value rule in CLAUDE.md exists to prevent.
 *
 * A substitution has no second copy to drift. It runs over whatever the words
 * are today, including words typed five minutes ago by a rep who has never
 * read this file.
 *
 * ## Where it runs
 *
 * On the way out, never on the way in. Nothing here writes to the database:
 * `buildPresentedDocument` applies it as the document is composed, so an
 * estimate written on a job that is later re-typed reads correctly the next
 * time it is presented rather than carrying yesterday's word. The stored row
 * is the office's copy; this is what the customer reads.
 *
 * That also makes it idempotent by construction, which matters more than it
 * sounds: no replacement below contains any phrase the table matches, so
 * running it over already-commercial text changes nothing.
 */

import { isCommercialJob } from "./autoExpenses.js";

/**
 * What a word becomes on a commercial job, longest phrase first.
 *
 * Order is load-bearing. The phrases are compiled into one alternation and a
 * regular expression tries alternatives left to right, so "home equity" has to
 * stand before "your home" or "No Home Equity Required" would become
 * "No Home Equity Required" with the wrong half rewritten. `commercialWording`
 * sorts by length rather than trusting this list to stay in order, and a test
 * asserts the sort does what the comment claims.
 *
 * Every entry is a word that is *wrong* on a commercial building, not merely
 * warm. "Curb appeal" stays: commercial real estate says it too. "Family"
 * appears only in the agreement, which is handled by whole paragraphs in
 * `contract.ts` rather than by this table — see the note at the foot of this
 * file.
 */
export const COMMERCIAL_WORDING: readonly (readonly [
  residential: string,
  commercial: string,
])[] = [
  // "No Home Equity Required" on a financing panel a property manager is
  // reading is not a softer way of saying the same thing — it names a
  // residential lending product. The offer is the same without the word.
  ["home equity", "equity"],
  ["homeowner's", "property owner's"],
  ["homeowners", "property owners"],
  ["homeowner", "property owner"],
  ["home protection", "property protection"],
  ["your home", "your property"],
  ["your house", "your building"],
  // A warehouse has no attic. This is the one pair here that replaces a word
  // that is simply absent rather than a word that is impolite, and it is the
  // one worth reading twice — see the PR.
  ["attic ventilation", "roof ventilation"],
  ["your attic", "the roof space"],
];

/** The pairs, longest phrase first, so an alternation matches the longest. */
const ordered = [...COMMERCIAL_WORDING].sort(
  (a, b) => b[0].length - a[0].length,
);

const replacements = new Map(
  ordered.map(([residential, commercial]) => [residential, commercial]),
);

const escape = (phrase: string) =>
  phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * One pass, so a replacement can never be re-matched by a later pair.
 *
 * Run pair by pair instead, "your home" → "your property" would then be read
 * by a pair matching "property" and the sentence would keep changing for as
 * long as the table had entries. One alternation, one pass, one substitution
 * per word.
 */
const pattern = new RegExp(
  `\\b(${ordered.map(([residential]) => escape(residential)).join("|")})\\b`,
  "gi",
);

/** Whether the matched text began with a capital, so the swap can keep it. */
const capitalised = (text: string) => text[0] === text[0]?.toUpperCase();

const matchCase = (matched: string, replacement: string): string =>
  capitalised(matched)
    ? (replacement[0] ?? "").toUpperCase() + replacement.slice(1)
    : replacement;

/**
 * The same sentence, worded for a building rather than a home.
 *
 * Case is carried across so "Homeowners who want" reads "Property owners who
 * want" rather than shouting or whispering mid-sentence. Only the first letter
 * — a phrase in a heading is title case in the source too, and rewriting the
 * whole match upper would turn "Attic Ventilation" into "ROOF VENTILATION".
 */
export function commercialWording(text: string): string {
  return text.replace(pattern, (matched) => {
    const replacement = replacements.get(matched.toLowerCase());
    return replacement ? matchCase(matched, replacement) : matched;
  });
}

/**
 * The wording this job's customer should read.
 *
 * The one function the document builder calls. A residential job — and a job
 * with no type at all, which is every job before `job_type` was set — is left
 * exactly as it was written, because the residential wording is the wording
 * this company sells in and there is nothing to fix.
 */
export function wordingFor(
  jobType: string | null | undefined,
  text: string,
): string {
  return isCommercialJob(jobType) ? commercialWording(text) : text;
}

/**
 * The same, for text that may not be there.
 *
 * Half the copy on a proposal is nullable — a block with no body, an upgrade
 * with no blurb — and a null that came back as the string "null" would print.
 */
export function wordingForOptional(
  jobType: string | null | undefined,
  text: string | null | undefined,
): string | null {
  if (text === null || text === undefined) return null;
  return wordingFor(jobType, text);
}

/*
 * What this deliberately does not touch
 *
 * **The agreement.** It said "the Customer's family", "your house originally"
 * and "rated for driveway usage", and Jonathon asked for those on 2026-08-28
 * too — but not through this table. `contract.ts` swaps five whole paragraphs
 * instead, because its words are code rather than editable data and because a
 * regular expression inside a liability release changes who is covered in a
 * document somebody signs. ADR 0072 has the reasoning.
 *
 * **Anything the customer typed or that names them.** The substitution runs
 * over composed copy inside `buildPresentedDocument`, never over a name, an
 * address or a price. "Homeward Bound LLC" is a customer, not a phrase to fix.
 */
